import React from 'react';
import ReactDOM from 'react-dom/client';
// Bootstrap's reboot and the few of its classes the pages use, bundled, first
// in the cascade as the CDN copy of all of Bootstrap was until 2026-10
// (scripts/bootstrap-subset.js says what is in it and why).
import './styles/bootstrap-subset.css';
import './index.css';
import './modal-fixes.css';
import './i18n';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import store from './Store';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { ErrorBoundary } from './Components/ErrorBoundary';
import { reportClientError } from './utils/errorReporting';
import { installDomTranslateGuard } from './utils/domTranslateGuard';
import { installStableViewportHeight } from './utils/stableViewport';
import { installInAppBackGuard } from './utils/inAppBackGuard';
import { deviceUiLanguage, initialUiLanguage } from './utils/shopLanguage';
import { getDomainHost, isDomainMode } from './utils/domainMode';
import { adoptHandoffVisitorKey } from './utils/visitorKey';
import { applyWalletTestFromUrl } from './utils/walletTest';

// Before React renders: stop the browser auto-translate removeChild/insertBefore
// white-screen crash.
installDomTranslateGuard();

// `?walletTest=on|off` marks this device's in-app browser for the owner's Apple Pay test (utils/walletTest, 2026-11-02).
applyWalletTestFromUrl();

// Before the first event: a landing handed over from the app's browser continues the same
// visitor (utils/visitorKey — the hand-off carries the key the in-app page had).
if (typeof window !== 'undefined' && window.location) adoptHandoffVisitorKey(window.location.search);

// Before the first paint: in Instagram's / Facebook's in-app browser the photo stage is sized from a
// height that does not grow when the app's toolbar collapses (utils/stableViewport).
installStableViewportHeight();

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    reportClientError(event.error || new Error(event.message), { type: 'error' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const err = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    reportClientError(err, { type: 'unhandledrejection' });
  });
  // Tranzila top-level redirects sometimes encode `?` as `%3F` and the entire
  // query string into the pathname, e.g.
  //   /payments/iframe-callback%3FResponse%3D141%26...
  // That doesn't match the SPA route → falls through to login → bounces to
  // /home. Detect and rewrite to a proper URL before React Router boots.
  const rawPath = window.location.pathname || '';
  if (rawPath.startsWith('/payments/iframe-callback%')) {
    const decoded = decodeURIComponent(rawPath);
    const idx = decoded.indexOf('?');
    if (idx > 0) {
      const cleanPath = decoded.slice(0, idx);
      const search = decoded.slice(idx);
      window.history.replaceState({}, '', cleanPath + search);
    }
  }
  // After that rewrite, never before it: in Instagram's / Facebook's in-app browser the entry the
  // shopper arrived on is marked as their root and a second entry is pushed over it, so an edge
  // swipe has somewhere to go other than out of the browser (utils/inAppBackGuard). The root must
  // carry the address React Router boots on, not the one Tranzila mangled.
  installInAppBackGuard();
}

function getInitialLanguage() {
  if (typeof window === 'undefined') return 'en';
  // The device's language, or -- on a shop's own page whose languages this
  // browser has already seen -- the shop's (utils/shopLanguage).
  return initialUiLanguage({
    pathname: window.location.pathname,
    domainMode: isDomainMode(),
    hostname: getDomainHost(),
  });
}

function init() {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    console.error('Root element #root not found. Ensure the HTML includes <div id="root"></div>.');
    return;
  }
  const root = ReactDOM.createRoot(rootEl);
  root.render(
    <ErrorBoundary>
      <Provider store={store}>
        <I18nextProvider i18n={i18n}>
          <App />
        </I18nextProvider>
      </Provider>
    </ErrorBoundary>
  );
  reportWebVitals();
}

function bootstrap() {
  const initialLang = getInitialLanguage();
  const initDir = ['ar', 'he'].includes(initialLang) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('lang', initialLang);
  document.documentElement.setAttribute('dir', initDir);
  // Remembered as the DEVICE default, never the shop's: a shop's language is
  // re-decided on its own pages and must not follow the visitor elsewhere.
  if (!localStorage.getItem('language')) try { localStorage.setItem('language', deviceUiLanguage()); } catch (e) {}
  i18n.changeLanguage(initialLang).then(() => init());
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
